<template>
  <div class="properties-content">
      <!-- 音频状态指示器 -->
      <div class="audio-status">
        <div class="status-item">
          <div class="status-label">{{$translate('audioStatus')}}</div>
          <div class="status-indicator active">
            <div class="status-dot"></div>
            <span>{{$translate('normal')}}</span>
          </div>
        </div>
        <div class="status-item">
          <div class="status-label">{{$translate('connectionStatus')}}</div>
          <div class="status-indicator active">
            <div class="status-dot"></div>
            <span>{{$translate('connected')}}</span>
          </div>
        </div>
      </div>

      <!-- 扬声器音量 -->
      <div class="property-item volume-item">
        <div class="property-label">
          <svg-icon name="volume" class="property-icon"></svg-icon>
          {{$translate('speakerVolume')}}
        </div>
        <div class="volume-control">
          <div class="volume-slider-container">
            <div class="volume-track" ref="volumeTrack" @click="handleTrackClick">
              <div class="volume-progress" :style="{ width: volume + '%' }"></div>
              <div
                class="volume-thumb"
                :style="{ left: volume + '%' }"
                @mousedown="startDrag"
              ></div>
            </div>
            <div class="volume-display">
              <span class="volume-value">{{ volume }}%</span>
              <div class="volume-bars">
                <div
                  class="volume-bar"
                  v-for="i in 10"
                  :key="i"
                  :class="{ active: i <= Math.ceil(volume / 10) }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 音频编码格式 -->
      <div class="property-item">
        <div class="property-label">
          <svg-icon name="codec" class="property-icon"></svg-icon>
          {{$translate('audioCodecFormat')}}
        </div>
        <div class="property-value readonly">
          <div class="value-display">{{$translate('pcmaCodec')}}</div>
          <div class="tech-indicator"></div>
        </div>
      </div>

      <!-- 采样率 -->
      <div class="property-item">
        <div class="property-label">
          <svg-icon name="frequency" class="property-icon"></svg-icon>
          {{$translate('sampleRate')}}
        </div>
        <div class="property-value readonly">
          <div class="value-display">8000Hz</div>
          <div class="tech-indicator"></div>
        </div>
      </div>

      <!-- 声道 -->
      <div class="property-item">
        <div class="property-label">
          <svg-icon name="channel" class="property-icon"></svg-icon>
          {{$translate('audioChannel')}}
        </div>
        <div class="property-value readonly">
          <div class="value-display">{{$translate('monoChannel')}}</div>
          <div class="tech-indicator"></div>
        </div>
      </div>
  </div>
</template>

<script>
import {buildWebSocketUrl, connectWithUrl, sendMsg} from '@/utils/websocket';
import { getTenantId } from "@/libs/util";
import api from '@/api'

export default {
  name: 'AudioPropertiesPanel',
  props: {
    selectedRobot: {
      type: Object,
      default: () => ({})
    },
    selectedRobotId: {
      type: [String, Number],
      default: null
    }
  },
  watch: {
    selectedRobotId: {
      handler(newVal, oldVal) {
        if (newVal) {
          this.volume = 0
        }
      },
      immediate: true
    }
  },
  data() {
    return {
      volume: 0, // 默认音量0%
      isDragging: false,
      dragStartX: 0,
      dragStartVolume: 0,
      musicSocket:null
    }
  },
  computed: {
    userInfo() {
      return this.$store.state.user.userInfo;
    }
  },
  created() {
    document.addEventListener('mousemove', this.handleDrag);
    document.addEventListener('mouseup', this.stopDrag);
    this.initWebSocket()
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this.handleDrag);
    document.removeEventListener('mouseup', this.stopDrag);
    this.musicSocket.close()
  },
  methods: {
    // 开始拖拽
    startDrag(event) {
      this.isDragging = true;
      this.dragStartX = event.clientX;
      this.dragStartVolume = this.volume;
      event.preventDefault();
    },

    // 处理拖拽
    handleDrag(event) {
      if (!this.isDragging) return;

      const track = this.$refs.volumeTrack;
      if (!track) return;

      const rect = track.getBoundingClientRect();
      const deltaX = event.clientX - this.dragStartX;
      const deltaPercent = (deltaX / rect.width) * 100;

      let newVolume = this.dragStartVolume + deltaPercent;
      newVolume = Math.max(0, Math.min(100, newVolume));

      this.volume = Math.round(newVolume);
      this.handleTrackClick();
    },

    // 停止拖拽
    stopDrag() {
      this.isDragging = false;
    },

    // 点击音量轨道
    handleTrackClick(event) {
      if (this.isDragging) return;

      const track = this.$refs.volumeTrack;
      const rect = track.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const newVolume = Math.round((clickX / rect.width) * 100);

      this.volume = Math.max(0, Math.min(100, newVolume));
      this.handleVolumeChange()
    },
    // 处理音量变化
    handleVolumeChange() {
      api.postData('notice-server/cmd/audio/setHardwareVolume',
        {
          robotId:this.selectedRobotId,
          data: {
            volume: this.volume,
            deviceType: 1
          }
        },this).then(res => {
        if (res.state === 0) {
          this.$Notice.error({
            title: this.$translate('tip'),
            desc: res.msg
          });
        } else {
          this.$Notice.info({
            title: this.$translate('volumeSettings'),
            desc: `${this.currentRobot.name} ${this.$translate('volumeSetTo')} ${volume}%`
          });
        }
      });
    },
    getVolume(){
      sendMsg("musicSocketUrl",JSON.stringify({
        tenantId:getTenantId(),
        robotId:this.selectedRobotId,
        language:this.$store.state.app.language,
        data:1,
        type:'get_hardware_volume'
      }))
    },
    // 初始化 WebSocket 连接
    initWebSocket() {
      connectWithUrl('/sound', "musicSocketUrl", JSON.stringify({
        tenantId: getTenantId(),
        type: `init:${this.userInfo.userId}`,
        language: this.$store.state.app.language,
      }), (data) => {
        this.handleWebSocketMessage(data);
      }, (socket) => {
        this.musicSocket = socket;
        this.getVolume()
      });
    },

    // 处理 WebSocket 消息
    handleWebSocketMessage(message) {
      try {
        console.log(message);
        const data = typeof message === 'string' ? JSON.parse(message) : message;

        switch(data.type) {
          case 'hardware_volume':
            this.updateHardwareVolume(data);
            break;
          default:
            // 统一处理成功和错误消息
            if (data.type === 'cmd_resp_set_hardware_volume_success') {
              this.$Notice.success({
                title: this.$translate('success'),
                desc: `${this.$translate('volumeUpdateSuccess')}: ${data.data || ''}`
              });
              this.getVolume();
            } else if (data.type === 'cmd_resp_set_hardware_volume_error') {
              this.$Notice.error({
                title: this.$translate('error'),
                desc: `${this.$translate('volumeUpdateFailed')}: ${data.data || ''}`
              });
            }
            break;
        }
      } catch (error) {
        console.error('处理 WebSocket 消息出错:', error);
      }
    },
    updateHardwareVolume(data){
      this.volume = data.data.volume;
    }
  }
}
</script>

<style lang="less" scoped>
.audio-properties-panel {
  position: relative;
  width: 100%;
  height: 100%;
  background: rgba(10, 25, 50, 0.8);
  border-radius: 15px;
  border: 1px solid rgba(0, 191, 255, 0.3);
  backdrop-filter: blur(10px);
  box-shadow:
    0 0 20px rgba(0, 191, 255, 0.1),
    inset 0 0 10px rgba(0, 191, 255, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 科技感边角 */
.tech-corner {
  position: absolute;
  width: 20px;
  height: 20px;
  border: 1px solid rgba(0, 191, 255, 0.6);
  z-index: 5;
}

.top-left {
  top: 0;
  left: 0;
  border-right: none;
  border-bottom: none;
  border-top-left-radius: 5px;
}

.top-right {
  top: 0;
  right: 0;
  border-left: none;
  border-bottom: none;
  border-top-right-radius: 5px;
}

.bottom-left {
  bottom: 0;
  left: 0;
  border-right: none;
  border-top: none;
  border-bottom-left-radius: 5px;
}

.bottom-right {
  bottom: 0;
  right: 0;
  border-left: none;
  border-top: none;
  border-bottom-right-radius: 5px;
}

.panel-header {
  padding: 20px;
  border-bottom: 1px solid rgba(0, 191, 255, 0.2);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.title {
  font-size: 18px;
  font-weight: bold;
  color: #00bfff;
  display: flex;
  align-items: center;
  gap: 10px;
}

.title-icon {
  font-size: 20px;
  color: #00bfff;
}

.robot-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.robot-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid rgba(0, 191, 255, 0.5);
}

.robot-name {
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
}

.header-decoration {
  position: absolute;
  bottom: 0;
  left: 20px;
  right: 20px;
  height: 2px;
  background: linear-gradient(90deg, transparent, #00bfff, transparent);
  animation: glow-line 2s infinite alternate;
}

.properties-content {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
}

.properties-content::-webkit-scrollbar {
  width: 6px;
}

.properties-content::-webkit-scrollbar-track {
  background: rgba(0, 191, 255, 0.1);
  border-radius: 3px;
}

.properties-content::-webkit-scrollbar-thumb {
  background: rgba(0, 191, 255, 0.3);
  border-radius: 3px;
}

.properties-content::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 191, 255, 0.5);
}

.property-item {
  margin-bottom: 15px;
  padding: 20px;
  background: rgba(15, 35, 70, 0.6);
  border: 1px solid rgba(0, 191, 255, 0.2);
  border-radius: 10px;
  transition: all 0.3s ease;
}

.property-item:hover {
  border-color: rgba(0, 191, 255, 0.4);
  box-shadow: 0 0 15px rgba(0, 191, 255, 0.1);
}

.property-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 15px;
}

.property-icon {
  font-size: 18px;
  color: #00bfff;
}

.property-value {
  position: relative;
}

.property-value.readonly {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.value-display {
  font-size: 18px;
  font-weight: 500;
  color: #00bfff;
  padding: 10px 15px;
  background: rgba(0, 191, 255, 0.1);
  border: 1px solid rgba(0, 191, 255, 0.3);
  border-radius: 8px;
  flex: 1;
}

.tech-indicator {
  width: 40px;
  height: 20px;
  background: linear-gradient(90deg, transparent, #00bfff, transparent);
  border-radius: 10px;
  animation: tech-pulse 2s infinite;
  margin-left: 15px;
}
.volume-item {
  background: rgba(15, 35, 70, 0.8);
  border-color: rgba(0, 191, 255, 0.4);
}

.volume-control {
  margin-top: 10px;
}

.volume-slider-container {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.volume-track {
  position: relative;
  width: 100%;
  height: 8px;
  background: rgba(0, 191, 255, 0.2);
  border-radius: 4px;
  cursor: pointer;
  overflow: hidden;
}

.volume-progress {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: linear-gradient(90deg, #00ff88, #00bfff);
  border-radius: 4px;
  transition: width 0.2s ease;
  box-shadow: 0 0 10px rgba(0, 191, 255, 0.5);
}

.volume-thumb {
  position: absolute;
  top: 50%;
  width: 20px;
  height: 20px;
  background: #00bfff;
  border: 3px solid #ffffff;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  cursor: grab;
  transition: all 0.2s ease;
  box-shadow: 0 0 15px rgba(0, 191, 255, 0.6);
}

.volume-thumb:hover {
  transform: translate(-50%, -50%) scale(1.2);
  box-shadow: 0 0 20px rgba(0, 191, 255, 0.8);
}

.volume-thumb:active {
  cursor: grabbing;
  transform: translate(-50%, -50%) scale(1.1);
}

.volume-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.volume-value {
  font-size: 24px;
  font-weight: bold;
  color: #00bfff;
  text-shadow: 0 0 10px rgba(0, 191, 255, 0.5);
}

.volume-bars {
  display: flex;
  gap: 3px;
  align-items: end;
}

.volume-bar {
  width: 4px;
  height: 20px;
  background: rgba(0, 191, 255, 0.2);
  border-radius: 2px;
  transition: all 0.3s ease;
}

.volume-bar.active {
  background: linear-gradient(to top, #00ff88, #00bfff);
  box-shadow: 0 0 5px rgba(0, 191, 255, 0.5);
  animation: bar-pulse 1s infinite alternate;
}

.audio-status {
  margin-bottom: 15px;
  padding: 20px;
  background: rgba(15, 35, 70, 0.6);
  border: 1px solid rgba(0, 191, 255, 0.2);
  border-radius: 10px;
  display: flex;
  gap: 30px;
}

.status-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.status-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 500;
}

.status-indicator.active {
  color: #00ff88;
}

.status-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #00ff88;
  box-shadow: 0 0 10px rgba(0, 255, 136, 0.6);
  animation: pulse-green 2s infinite;
}

.no-robot-selected {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px;
}

.empty-icon {
  margin-bottom: 20px;
}

.robot-icon {
  font-size: 64px;
  color: rgba(0, 191, 255, 0.3);
  animation: float 3s ease-in-out infinite;
}

.empty-message {
  font-size: 20px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 10px;
}

.empty-description {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.5;
}

/* 动画定义 */
@keyframes glow-line {
  0% {
    opacity: 0.5;
    box-shadow: 0 0 5px rgba(0, 191, 255, 0.3);
  }
  100% {
    opacity: 1;
    box-shadow: 0 0 15px rgba(0, 191, 255, 0.6);
  }
}

@keyframes tech-pulse {
  0% {
    opacity: 0.3;
    transform: scaleX(0.8);
  }
  50% {
    opacity: 1;
    transform: scaleX(1.2);
  }
  100% {
    opacity: 0.3;
    transform: scaleX(0.8);
  }
}

@keyframes bar-pulse {
  0% {
    height: 20px;
    opacity: 0.8;
  }
  100% {
    height: 24px;
    opacity: 1;
  }
}

@keyframes pulse-green {
  0% {
    box-shadow: 0 0 10px rgba(0, 255, 136, 0.6);
  }
  50% {
    box-shadow: 0 0 20px rgba(0, 255, 136, 0.9);
  }
  100% {
    box-shadow: 0 0 10px rgba(0, 255, 136, 0.6);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}
</style>
